import {
  Apps20Filled,
  Apps20Regular,
  bundleIcon,
  Home20Filled,
  Home20Regular,
  LockMultiple20Filled,
  LockMultiple20Regular,
  Organization20Filled,
  Organization20Regular,
  PeopleCommunity20Filled,
  PeopleCommunity20Regular,
  Person20Filled,
  Person20Regular,
  Agents20Filled,
  Agents20Regular,
} from '@fluentui/react-icons';
import { useAuth } from '@iamexperiences/feature-auth';
import { INavigationItem } from '@iamexperiences/feature-suite-left-nav';
import { useLocalization } from '../atoms/useLocalization';
import React, { useMemo } from 'react';

import { useTelemetry } from '@iamexperiences/ecos-telemetry';
import { useAreGroupsEnabled, useIsApprovalManagementAvailable } from './useLeftNavGroupConditions';
import { useIsInternalUser } from './useLeftNavMyAccessConditions';
import { NAV_CONFIG } from './shared/constants';
import { settingsManager } from '../../shared/settings';
import { isFeatureEnabled } from '../../shared/SettingsManager';
import { useCanViewAdminUnits } from './useLeftNavMyStaffConditions';
import { useGetCurrentOrganization } from '../client-header-tenant';
import { useLeftNavAgentConditions } from './useLeftNavAgentConditions';

const Person = bundleIcon(Person20Filled, Person20Regular);
const Home = bundleIcon(Home20Filled, Home20Regular);
const Group = bundleIcon(PeopleCommunity20Filled, PeopleCommunity20Regular);
const Lock = bundleIcon(LockMultiple20Filled, LockMultiple20Regular);
const Apps = bundleIcon(Apps20Filled, Apps20Regular);
const Staff = bundleIcon(Organization20Filled, Organization20Regular);
const Agent = bundleIcon(Agents20Filled, Agents20Regular);

export function useLeftNavData(): INavigationItem[] {
  const { auth } = useAuth();
  const [t] = useLocalization();
  const telemetry = useTelemetry();
  const groupsEnabledQuery = useAreGroupsEnabled();
  const approvalManagementQuery = useIsApprovalManagementAvailable();
  const isInternalUser = useIsInternalUser();
  const canViewAUs = useCanViewAdminUnits();
  const agentConditions = useLeftNavAgentConditions();

  const tenantId = auth.user?.tenantId;
  const { data: currentOrganization } = useGetCurrentOrganization(tenantId ?? '');

  const myAccessUrl = settingsManager.get('myAccessUrl');
  const myGroupsUrl = settingsManager.get('myGroupsUrl');
  const myAccountUrl = settingsManager.get('meControlViewAccountLink');
  const myStaffUrl = settingsManager.get('myStaffUrl');
  const signInsUrl = settingsManager.get('signInsUrl');
  const changePasswordFwdLink = settingsManager.get('changePasswordFwdLink');
  const manageAgentNavItemEnabled = settingsManager.get('manageAgentNavItemEnabled');
  const myAgentsUrl = settingsManager.get('myAgentsUrl');
  const homeLink = settingsManager.get('homeLink');

  return useMemo(() => {
    const myStarOnClick = (href: string | undefined, targetApp: string, addTenantId: boolean = true) => {
      telemetry.reportCustomEvent('left-nav/switch-app', {
        currentApp: 'My Apps',
        targetApp
      });

      if (href) {
        const url = new URL(href);
        if (addTenantId) {
          url.searchParams.set('tenantId', auth.user?.tenantId || '');
        }
        window.location.href = url.toString();
      }
    };

    const myAccountChildNavigationitems: INavigationItem[] = [];

    myAccountChildNavigationitems.push({
      navLinkTitle: t('securityInfo'),
      ariaLabel: t('securityInfo'),
      navClickAction: () => {
        myStarOnClick(`${signInsUrl}/security-info`, 'Security Info');
      },
      navValue: NAV_CONFIG.securityInfo
    });


    myAccountChildNavigationitems.push(
      {
        navLinkTitle: t('devices'),
        ariaLabel: t('devices'),
        navClickAction: () => myStarOnClick(`${myAccountUrl}/device-list`, 'Devices'),
        navValue: NAV_CONFIG.devices
      },
      {
        navLinkTitle: t('changePassword'),
        ariaLabel: t('changePassword'),
        navClickAction: () => {
          myStarOnClick(changePasswordFwdLink, 'Change Password');
        },
        navValue: NAV_CONFIG.password
      },
      {
        navLinkTitle: t('organizations'),
        ariaLabel: t('organizations'),
        navClickAction: () => myStarOnClick(`${myAccountUrl}/organizations`, 'Organizations'),
        navValue: NAV_CONFIG.organizations
      },
      {
        navLinkTitle: t('settingsAndPrivacyCardTitle'),
        ariaLabel: t('settingsAndPrivacyCardTitle'),
        navClickAction: () => myStarOnClick(`${myAccountUrl}/settingsandprivacy`, 'Settings and Privacy'),
        navValue: NAV_CONFIG.settingsAndPrivacy
      }
    );

      myAccountChildNavigationitems.push({
        navLinkTitle: t('recentActivity'),
        ariaLabel: t('recentActivity'),
        navClickAction: () => {
          myStarOnClick(
            signInsUrl,
            'My Sign-Ins'
          );
        },
        navValue: NAV_CONFIG.mySignIns
      });

 const groupNavigationItems: INavigationItem[] = [];
    if (groupsEnabledQuery.isSuccess && groupsEnabledQuery.data) {
      groupNavigationItems.push(
        {
          navLinkTitle: t('overview'),
          ariaLabel: t('overview'),
          navClickAction: () => {
            myStarOnClick(myGroupsUrl, 'myGroups');
          },
          navValue: NAV_CONFIG.myGroupsOverview,
        },
        {
          navLinkTitle: t('groupsIOwn'),
          ariaLabel: t('groupsIOwn'),
          navClickAction: () => {
            myStarOnClick(`${myGroupsUrl}/groups-i-own`, 'myGroups');
          },
          navValue: NAV_CONFIG.groupsIOwn,
        },
        {
          navLinkTitle: t('groupsIBelongTo'),
          ariaLabel: t('groupsIBelongTo'),
          navClickAction: () => {
            myStarOnClick(`${myGroupsUrl}/groups-i-belong-to`, 'myGroups');
          },
          navValue: NAV_CONFIG.groupsIAmIn,
        },
      );
    }

    if (
      groupsEnabledQuery.isSuccess &&
      groupsEnabledQuery.data
    ) {
      groupNavigationItems.push({
        navLinkTitle: t('deletedGroups'),
        ariaLabel: t('deletedGroups'),
        navClickAction: () => {
          myStarOnClick(`${myGroupsUrl}/deleted-groups`, 'myGroups');
        },
        navValue: NAV_CONFIG.deletedGroups,
      });
    }
    if (approvalManagementQuery.isSuccess && approvalManagementQuery.data) {
      groupNavigationItems.push({
        navLinkTitle: t('requests'),
        ariaLabel: t('requests'),
        navClickAction: () => {
          myStarOnClick(`${myGroupsUrl}/requests`, 'myGroups');
        },
        navValue: NAV_CONFIG.requests,
      });
    }

    const accessNavigationItems: INavigationItem[] = [
      {
        navLinkTitle: t('overview'),
        ariaLabel: t('overview'),
        navValue: NAV_CONFIG.myAccessOverview,
        navClickAction: () => myStarOnClick(myAccessUrl, 'myAccess')
      },
      {
        navLinkTitle: t('accessPackages'),
        ariaLabel: t('accessPackages'),
        navValue: NAV_CONFIG.accessPackages,
        navClickAction: () => myStarOnClick(`${myAccessUrl}/#/access-packages`, 'myAccess')
      },
      {
        navLinkTitle: t('requestHistory'),
        ariaLabel: t('requestHistory'),
        navValue: NAV_CONFIG.requestHistory,
        navClickAction: () => myStarOnClick(`${myAccessUrl}/#/request-history`, 'myAccess')
      }];

    if (isInternalUser) {
      accessNavigationItems.push(
        {
          navLinkTitle: t('approvals'),
          ariaLabel: t('approvals'),
          navValue: NAV_CONFIG.approvals,
          navClickAction: () => myStarOnClick(`${myAccessUrl}/#/request-approval`, 'myAccess')
        },
        {
          navLinkTitle: t('accessReviews'),
          ariaLabel: t('accessReviews'),
          navValue: NAV_CONFIG.accessReviews,
          navClickAction: () => myStarOnClick(`${myAccessUrl}/#/access-reviews`, 'myAccess')
        }
      );
    };

    accessNavigationItems.push({
      navLinkTitle: t('showMoreInMyAccess'),
      ariaLabel: t('showMoreInMyAccess'),
      navValue: NAV_CONFIG.showMore,
      navClickAction: () => myStarOnClick(`${myAccessUrl}`, 'myAccess')
    });

    const navigationItems: INavigationItem[] = [
      {
        navLinkTitle: t('Home'),
        ariaLabel: t('Home'),
        navValue: NAV_CONFIG.home,
        icon: <Home />,
        navClickAction: () => myStarOnClick(homeLink, 'home'),
      },
      {
        navLinkTitle: t('myAccount'),
        ariaLabel: t('myAccount'),
        navValue: NAV_CONFIG.myAccount,
        icon: <Person />,
        navChildren: myAccountChildNavigationitems
      }
    ];

      navigationItems.push({
        navLinkTitle: t('My Apps'),
        ariaLabel: t('My Apps'),
        navValue: NAV_CONFIG.myApps,
        icon: <Apps />,
      });


    if(myStaffUrl && canViewAUs)
      navigationItems.push({
        navLinkTitle: t('My Staff'),
        ariaLabel: t('My Staff'),
        navValue: NAV_CONFIG.myStaff,
        icon: <Staff />,
        navClickAction: () => myStarOnClick(myStaffUrl, 'myStaff')
      });

    if (
      myGroupsUrl &&
      groupsEnabledQuery.isSuccess &&
      groupsEnabledQuery.data
    ) {
      navigationItems.push({
        navLinkTitle: t('myGroups'),
        ariaLabel: t('myGroups'),
        navValue: NAV_CONFIG.myGroups,
        icon: <Group />,
        navChildren: groupNavigationItems
      });
    }

    if (myAccessUrl && currentOrganization?.hasP2License) {
      navigationItems.push({
        navLinkTitle: t('myAccess'),
        ariaLabel: t('myAccess'),
        navValue: NAV_CONFIG.myAccess,
        icon: <Lock />,
        navChildren: accessNavigationItems
      });
    }

    const enableManageAgents = agentConditions.hasAnyAgents && isFeatureEnabled(manageAgentNavItemEnabled, auth.user?.tenantId);
    if (enableManageAgents && myAgentsUrl) {
      navigationItems.push({
        navLinkTitle: t('manageAgents'),
        ariaLabel: t('manageAgents'),
        navClickAction: () => myStarOnClick(myAgentsUrl, 'manageAgents', false),
        navValue: NAV_CONFIG.manageAgents,
        icon: <Agent />,
      });
    }

    return navigationItems;
  }, [
    t,
    groupsEnabledQuery.isSuccess,
    groupsEnabledQuery.data,
    approvalManagementQuery.isSuccess,
    approvalManagementQuery.data,
    myGroupsUrl,
    myAccessUrl,
    myAccountUrl,
    myAgentsUrl,
    agentConditions.hasAnyAgents,
    canViewAUs,
    isInternalUser,
    signInsUrl,
    telemetry,
    auth.user?.tenantId,
    homeLink
  ]);
}
